export type PowerCategoryAccent = {
  hex: string;
  glow: string;
  textClass: string;
};

/**
 * Smiley representativo de cada categoria em `/powers` (tiles Explorar Categorias).
 * Usado também nos cards de categoria do Fairtrade.
 */
export const POWER_CATEGORY_SMILEY_CODES: Record<string, string> = {
  smiley: 'gline',
  function: 'pstyle',
  stickers: 'couple',
  group: 'gcontrol',
  games: 'gamer',
  hug: 'heartbreak',
  collection: 'supersummer',
  epic: 'ruby',
  jinx: 'jumblejinx',
};

export function getPowerCategorySmileyCode(category: string): string | undefined {
  return POWER_CATEGORY_SMILEY_CODES[category.trim().toLowerCase()];
}

/** Cores de destaque por categoria — usadas nos tiles e nos cards do catálogo. */
export const POWER_CATEGORY_ACCENTS: Record<string, PowerCategoryAccent> = {
  todos: { hex: '#6366f1', glow: 'rgba(99,102,241,0.28)', textClass: 'text-indigo-400' },
  smiley: { hex: '#facc15', glow: 'rgba(250,204,21,0.28)', textClass: 'text-yellow-400' },
  function: { hex: '#3b82f6', glow: 'rgba(59,130,246,0.28)', textClass: 'text-blue-400' },
  stickers: { hex: '#ec4899', glow: 'rgba(236,72,153,0.28)', textClass: 'text-pink-400' },
  group: { hex: '#06b6d4', glow: 'rgba(6,182,212,0.28)', textClass: 'text-cyan-400' },
  games: { hex: '#f97316', glow: 'rgba(249,115,22,0.28)', textClass: 'text-orange-400' },
  hug: { hex: '#ef4444', glow: 'rgba(239,68,68,0.28)', textClass: 'text-red-400' },
  collection: { hex: '#a855f7', glow: 'rgba(168,85,247,0.28)', textClass: 'text-purple-400' },
  epic: { hex: '#f59e0b', glow: 'rgba(245,158,11,0.28)', textClass: 'text-amber-400' },
  jinx: { hex: '#84cc16', glow: 'rgba(132,204,22,0.28)', textClass: 'text-lime-400' },
  limited: { hex: '#eab308', glow: 'rgba(234,179,8,0.28)', textClass: 'text-yellow-400' },
  unlimited: { hex: '#10b981', glow: 'rgba(16,185,129,0.28)', textClass: 'text-emerald-400' },
};

const FALLBACK_ACCENT = POWER_CATEGORY_ACCENTS.smiley;

export function getPowerCategoryAccent(category: string): PowerCategoryAccent {
  const key = category.trim().toLowerCase();
  return POWER_CATEGORY_ACCENTS[key] ?? FALLBACK_ACCENT;
}

export function getPowerCategoryAccentHex(category: string): string {
  return getPowerCategoryAccent(category).hex;
}

/** Estilo de badge alinhado aos tiles de categoria em `/powers`. */
export function getPowerCategoryBadgeStyle(category: string): {
  backgroundColor: string;
  borderColor: string;
  color: string;
  boxShadow: string;
} {
  const { hex, glow } = getPowerCategoryAccent(category);
  return {
    backgroundColor: `${hex}22`,
    borderColor: `${hex}66`,
    color: hex,
    boxShadow: `0 0 14px ${glow}`,
  };
}

/** Texto secundário legível sobre tiles escuros — tom claro da cor da categoria. */
export function getPowerCategoryMutedTextColor(hex: string): string {
  return `color-mix(in srgb, ${hex} 42%, white)`;
}

/** Superfície escura dos tiles «Explorar Categorias» — tom da categoria em fundo/borda. */
export function getPowerCategoryTileSurface(
  accent: PowerCategoryAccent,
  active: boolean
): { backgroundColor: string; borderColor: string; boxShadow: string } {
  return {
    backgroundColor: active ? `${accent.hex}26` : `${accent.hex}12`,
    borderColor: active ? accent.hex : `${accent.hex}52`,
    boxShadow: active
      ? `inset 0 1px 0 rgba(255,255,255,0.08), 0 10px 32px ${accent.glow}`
      : `inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 20px rgba(0,0,0,0.25)`,
  };
}

/** Pílulas da barra sticky de categorias em `/powers` (fundo claro). */
export function getPowerCategoryStickyFilterChipStyle(
  category: string,
  active: boolean
): { backgroundColor: string; borderColor: string; color: string; boxShadow: string } {
  const { hex, glow } = getPowerCategoryAccent(category);

  if (active) {
    return {
      backgroundColor: hex,
      borderColor: hex,
      color: '#ffffff',
      boxShadow: `0 2px 10px ${glow}`,
    };
  }

  return {
    backgroundColor: '#ffffff',
    borderColor: `color-mix(in srgb, ${hex} 38%, #e5e7eb)`,
    color: '#4b5563',
    boxShadow: 'none',
  };
}
